import { Alert } from '@skupreme/shared/ui';
import { ToastMessage } from '@utility/toast';
import { useEffect, useState } from 'react';
import { useMappingSuggestions } from '../../hooks/use-mapping-suggestions';
import { useRowActions } from '../../hooks/use-row-actions';
import { decisionOf, fetchHighConfidence } from '../../lib/bulk';
import { toListingRef } from '../../lib/listing-ref';
import { mappingErrorMessage } from '../../lib/mapping-errors';
import {
  addRows,
  removeIds,
  type RowSelection,
  toggleRow,
} from '../../lib/selection';
import {
  ANY,
  initialSuggestionFilter,
  SUGGESTIONS_PAGE_SIZE,
  type SuggestionFilter,
} from '../../lib/suggestion-params';
import type {
  ConfirmDecision,
  MappingCounts,
  MappingSuggestionRow,
  SuggestionView,
} from '../../lib/suggestion-types';
import type { ListingRef } from '../../lib/types';
import { CreateItemModal } from '../CreateItemModal';
import { ExcludeListingModal } from '../ExcludeListingModal';
import { PostgresItemPickerModal } from '../PostgresItemPickerModal';
import { BulkBar, type BulkAction } from './BulkBar';
import { BulkConfirmDialog } from './BulkConfirmDialog';
import { MappingFilters } from './MappingFilters';
import type { RowHandlers } from './SuggestionActions';
import { SuggestionsTable } from './SuggestionsTable';

type Empty = { title: string; description?: string };

const EMPTY: Record<SuggestionView, Empty> = {
  to_review: {
    title: 'Nothing to review',
    description: 'Listings with no suggestion are under No match.',
  },
  no_match: { title: 'Every listing has a suggestion' },
  skipped: { title: 'No skipped listings' },
};

const ALL_LINKED: Empty = { title: 'All listings are linked to products' };
/* No stored suggestion yet: the account was never checked, so zero says nothing about its listings. */
const NOT_CHECKED: Empty = {
  title: 'Not checked yet',
  description:
    'Press Check again to look for products for the listings that are not linked yet.',
};
const NO_RESULTS: Empty = { title: 'No listings match these filters' };

function emptyCopy(
  view: SuggestionView,
  counts: MappingCounts | undefined,
  isFiltered: boolean,
) {
  if (isFiltered) {
    return NO_RESULTS;
  }

  if (counts && counts.lastRefreshedAt === null && view !== 'skipped') {
    return NOT_CHECKED;
  }

  const waiting = counts && counts.toReview + counts.noMatch === 0;

  return view === 'to_review' && waiting ? ALL_LINKED : EMPTY[view];
}

type Picking = { row: MappingSuggestionRow; listing: ListingRef };

export type SuggestionsViewProps = {
  view: SuggestionView;
  counts?: MappingCounts;
  isReadOnly?: boolean;
  isActionsHidden?: boolean;
};

/** One queue of the Mapping tab: filters, bulk bar, rows and the dialogs their actions open. Keyed by view, so each queue starts clean. */
export function SuggestionsView({
  view,
  counts,
  isReadOnly,
  isActionsHidden,
}: SuggestionsViewProps) {
  const [filter, setFilter] = useState<SuggestionFilter>(
    initialSuggestionFilter,
  );
  const { rows, loading, error, total } = useMappingSuggestions(view, filter);
  const actions = useRowActions();
  const [selection, setSelection] = useState<RowSelection>(() => new Map());
  const [picking, setPicking] = useState<Picking | null>(null);
  const [creating, setCreating] = useState<ListingRef | null>(null);
  const [excluding, setExcluding] = useState<ListingRef | null>(null);
  const [bulkRows, setBulkRows] = useState<MappingSuggestionRow[] | null>(null);
  const [isSelectingHigh, setSelectingHigh] = useState(false);

  /* The last page can empty out after a write; step back to the one before. */
  const lastPage = Math.max(Math.ceil(total / SUGGESTIONS_PAGE_SIZE), 1);
  useEffect(() => {
    if (!loading && filter.page > lastPage) {
      setFilter(previous => ({ ...previous, page: lastPage }));
    }
  }, [loading, filter.page, lastPage]);

  /* Any filter but the page starts the list over. */
  const filterChanged = <K extends keyof SuggestionFilter>(
    key: K,
    value: SuggestionFilter[K],
  ) =>
    setFilter(previous => ({
      ...previous,
      [key]: value,
      page: key === 'page' ? (value as number) : 1,
    }));

  const drop = (ids: string[]) =>
    setSelection(previous => removeIds(previous, ids));

  const toggle = (row: MappingSuggestionRow) =>
    setSelection(previous => toggleRow(previous, row));

  const togglePage = () =>
    rows.every(row => selection.has(row.listingId))
      ? drop(rows.map(row => row.listingId))
      : setSelection(previous => addRows(previous, rows));

  const selectHighConfidence = async () => {
    setSelectingHigh(true);
    try {
      const high = await fetchHighConfidence(filter);
      setSelection(previous => addRows(previous, high));
    } catch (failure) {
      ToastMessage.error(mappingErrorMessage(failure));
    } finally {
      setSelectingHigh(false);
    }
  };

  const linkRow = async (decision: ConfirmDecision) => {
    if (await actions.link(decision)) {
      drop([decision.listingId]);
      setPicking(null);
    }
  };

  const moveRows = async (action: 'skip' | 'restore', ids: string[]) => {
    if (await actions.move(action, ids)) {
      drop(ids);
    }
  };

  const handlers: RowHandlers = {
    confirm: row => {
      const decision = decisionOf(row);
      return decision && linkRow(decision);
    },
    pick: row => setPicking({ row, listing: toListingRef(row) }),
    skip: row => moveRows('skip', [row.listingId]),
    restore: row => moveRows('restore', [row.listingId]),
    create: row => setCreating(toListingRef(row)),
    exclude: row => setExcluding(toListingRef(row)),
    choose: (row, candidate) =>
      linkRow({
        listingId: row.listingId,
        itemId: candidate.itemId,
        packQuantity: 1,
      }),
  };

  const bulk = (action: BulkAction) => {
    const chosen = [...selection.values()];

    return action === 'confirm'
      ? setBulkRows(chosen)
      : moveRows(
          action,
          chosen.map(row => row.listingId),
        );
  };

  const isFiltered =
    !!filter.search.trim() ||
    filter.connectionId !== ANY ||
    filter.band !== ANY;

  return (
    <div className="sk:flex sk:flex-col sk:gap-3">
      <MappingFilters
        filter={filter}
        onChange={filterChanged}
        showBand={view !== 'no_match'}
      />
      <BulkBar
        view={view}
        count={selection.size}
        onAction={bulk}
        onClear={() => setSelection(new Map())}
        onSelectHighConfidence={
          view === 'to_review' ? selectHighConfidence : undefined
        }
        isSelectingHigh={isSelectingHigh}
        isReadOnly={isReadOnly}
        isBusy={actions.isBusy}
      />
      {error ? <Alert tone="danger">{mappingErrorMessage(error)}</Alert> : null}
      <SuggestionsTable
        view={view}
        rows={rows}
        loading={loading}
        total={total}
        page={filter.page}
        onPageChange={page => filterChanged('page', page)}
        selectedIds={new Set(selection.keys())}
        onToggle={toggle}
        onTogglePage={togglePage}
        handlers={handlers}
        empty={emptyCopy(view, counts, isFiltered)}
        isReadOnly={isReadOnly}
        isActionsHidden={isActionsHidden}
        isBusy={actions.isBusy}
      />
      <PostgresItemPickerModal
        listing={picking?.listing ?? null}
        showPack
        defaultPack={picking?.row.packQuantity}
        onClose={() => setPicking(null)}
        onSelect={(itemId, packQuantity) =>
          picking &&
          linkRow({ listingId: picking.row.listingId, itemId, packQuantity })
        }
        isProcessing={actions.isBusy}
      />
      <CreateItemModal listing={creating} onClose={() => setCreating(null)} />
      <ExcludeListingModal
        listing={excluding}
        onClose={() => setExcluding(null)}
        onConfirm={async () => {
          if (excluding && (await actions.exclude(excluding.id))) {
            drop([excluding.id]);
            setExcluding(null);
          }
        }}
        isProcessing={actions.isBusy}
      />
      <BulkConfirmDialog
        rows={bulkRows}
        onClose={finished => {
          if (finished) {
            setSelection(new Map());
          }
          setBulkRows(null);
        }}
      />
    </div>
  );
}
